import { formatCurrencyLabelForNotice } from './currency';

export interface DisplayPricingFields {
  currency?: string | null;
  finalPrice?: number | null;
  unitPrice?: number | null;
  displayCurrency?: string | null;
  displayOriginalPrice?: number | null;
  displayFinalPrice?: number | null;
  displayUnitPrice?: number | null;
}

interface DisplayUnitPriceOptions {
  userCount?: number | null;
  durationInMonths?: number | null;
}

const isDefined = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

const getPositiveNumber = (value: number | null | undefined) =>
  typeof value === 'number' && value > 0 ? value : null;

const getNonNegativeNumber = (value: number | null | undefined) =>
  typeof value === 'number' && value >= 0 ? value : null;

export const getDisplayCurrency = (pricing: DisplayPricingFields) =>
  pricing.displayCurrency ?? pricing.currency ?? 'KRW';

export const getPaymentCurrency = (pricing: DisplayPricingFields) =>
  pricing.currency ?? getDisplayCurrency(pricing);

export const getDisplayOriginalPrice = (pricing: DisplayPricingFields) =>
  isDefined(pricing.displayOriginalPrice)
    ? pricing.displayOriginalPrice
    : pricing.finalPrice ?? 0;

export const getDisplayFinalPrice = (pricing: DisplayPricingFields) =>
  isDefined(pricing.displayFinalPrice)
    ? pricing.displayFinalPrice
    : pricing.finalPrice ?? 0;

export const getDisplayUnitPrice = (
  pricing: DisplayPricingFields,
  options?: DisplayUnitPriceOptions,
) => {
  if (isDefined(pricing.displayUnitPrice)) {
    return pricing.displayUnitPrice;
  }

  const userCount = getPositiveNumber(options?.userCount);
  const durationInMonths = getPositiveNumber(options?.durationInMonths);
  const displayOriginalPrice = getNonNegativeNumber(
    pricing.displayOriginalPrice,
  );

  if (userCount && durationInMonths && displayOriginalPrice !== null) {
    return displayOriginalPrice / userCount / durationInMonths;
  }

  return pricing.unitPrice ?? 0;
};

export const shouldShowPaymentCurrencyNotice = (
  paymentCurrency: string | null | undefined,
  displayCurrency: string | null | undefined,
) => {
  if (!paymentCurrency || !displayCurrency) return false;
  return (
    paymentCurrency.trim().toUpperCase() !==
    displayCurrency.trim().toUpperCase()
  );
};

export const getPaymentCurrencyNoticeLabel = (
  displayCurrency: string | null | undefined,
) => formatCurrencyLabelForNotice(displayCurrency);
